import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop } from "@stencil/core";

@Component({
    tag: "ux-sidebar",
    styleUrl: "sidebar.scss",
    shadow: false
})
export class Sidebar {
    private baseClass = "ux-sidebar";
    @Element() domNode!: HTMLUxSidebarElement;

    /** Set the name of the sidebar  */
    @Prop() readonly name: string = "";

    /** If present, uxSidebarVisibilityChanged event is emitted when escape key is pressed **/
    @Prop() readonly closeOnEscKey: boolean = false;

    /** If present, uxSidebarVisibilityChanged event is emitted when the backdrop is clicked **/
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly closeOnBackdropClick: boolean = true;

    /** Toggle transition **/
    @Prop() readonly transition: "slide" | "none" = "slide";

    /** Set alignment of sidebar **/
    @Prop() readonly alignment: "right" | "left" = "right";

    /** Disable backdrop **/
    @Prop() readonly disableBackdrop: boolean = false;

    /** Disable header with close icon **/
    @Prop() readonly disableCloseIcon: boolean = false;

    /** Flag that indicates if the sidebar top should be aligned with a header */
    @Prop() readonly alignHeader: boolean = false;

    /** Flag that indicates if the sidebar bottom should be aligned with a footer */
    @Prop() readonly alignFooter: boolean = false;

    /** Define z-index of backdrop */
    @Prop() readonly zIndex: number = 4001;

    /** Boolean to indicate if or not the sidebar is currently opened */
    @Prop({ mutable: true, reflect: true }) opened = false;

    /** Emitted when the sidebar is opened or closed */
    @Event({
        eventName: "uxSidebarVisibilityChanged",
        bubbles: true,
        cancelable: true
    })
    sidebarVisibilityChanged!: EventEmitter<{ name: string; opened: boolean }>;

    /** Toggles sidebar open or closed */
    @Method()
    async toggleSidebar(): Promise<void> {
        const newOpenState = !this.opened;
        const evt = this.sidebarVisibilityChanged.emit({
            name: this.name,
            opened: newOpenState
        });
        if (!evt.defaultPrevented) {
            this.opened = newOpenState;
        }
    }

    /** Listen for escape key  */
    @Listen("keydown", { target: "window" })
    handleKeyDown(event: KeyboardEvent) {
        if (this.closeOnEscKey && this.opened && event.key === "Escape") {
            this.toggleSidebar();
        }
    }

    private triggerElementClickHandler = (e: MouseEvent) => {
        const targetElement = (e.target as HTMLElement).closest("[data-trigger-sidebar]");
        if (targetElement === null) {
            return;
        }

        // Extract commands from the event, filtering for this sidebar
        const commands: string[][] = (targetElement.getAttribute("data-trigger-sidebar") || "")
            .split(";")
            .map((string) => string.split(","))
            .filter(([, name]) => name === this.name);

        if (commands.length > 0) {
            this.toggleSidebar();
        }
    };

    componentDidLoad() {
        document.addEventListener("click", this.triggerElementClickHandler);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.triggerElementClickHandler);
    }

    render() {
        const handleClickClose = () => {
            this.toggleSidebar();
        };

        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--open`]: this.opened
                }}
            >
                {this.disableBackdrop ? null : (
                    <div
                        class={{
                            [`${this.baseClass}__backdrop`]: true,
                            [`${this.baseClass}__backdrop--no-transition`]: this.transition === "none"
                        }}
                        // @ts-expect-error Check for typing
                        onClick={this.closeOnBackdropClick ? handleClickClose : null}
                        style={{ zIndex: `${this.zIndex}` }}
                    />
                )}
                <div
                    class={{
                        [`${this.baseClass}__inner`]: true,
                        [`${this.baseClass}__inner--no-transition`]: this.transition === "none",
                        [`${this.baseClass}__inner--align-left`]: this.alignment === "left",
                        [`${this.baseClass}__inner--align-header`]: this.alignHeader,
                        [`${this.baseClass}__inner--align-footer`]: this.alignFooter
                    }}
                >
                    {this.disableCloseIcon ? null : (
                        <div class={`${this.baseClass}__head`}>
                            <button type="button" class={`${this.baseClass}__close`} onClick={handleClickClose}>
                                <ux-icon name="cross" weight="bold" />
                            </button>
                        </div>
                    )}
                    <div class={`${this.baseClass}__slot`}>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
